import { computed, unref } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useConfig } from 'dashboard/composables/useConfig';
import { useUISettings } from 'dashboard/composables/useUISettings';
import {
  getUserPermissions,
  hasPermissions,
} from 'dashboard/helper/permissionsHelper';
import { PREMIUM_FEATURES } from 'dashboard/featureFlags';

import { INSTALLATION_TYPES } from 'dashboard/constants/installationTypes';

// SuperAdmin view-role override: when the real instance SuperAdmin
// selects a simulated view from the sidebar toggle, we synthesise the
// permissions array as if the current user had that role. This lets the
// existing native sidebar/route policies react automatically without
// hard-coding visibility per item.
const VIEW_ROLE_TO_PERMISSIONS = {
  agent: ['agent'],
  admin: ['administrator'],
  // super_admin -> no override, fall back to real permissions
};

// Features that are OFF by default for client accounts (developer-only in the
// standard product). When the real SuperAdmin simulates an agent/admin view
// from the sidebar toggle, these are treated as disabled so the developer can
// preview exactly what a client sees — even though the developer's own account
// has them all enabled. In the real 'super_admin' view everything stays visible.
const CLIENT_RESTRICTED_FEATURES = [
  'help_center',
  'agent_bots',
  'macros',
  'custom_attributes',
  'automations',
  'integrations',
  'campaigns',
];

export function usePolicy() {
  const user = useMapGetter('getCurrentUser');
  const isFeatureEnabled = useMapGetter('accounts/isFeatureEnabledonAccount');
  const isOnChatwootCloud = useMapGetter('globalConfig/isOnChatwootCloud');
  const isACustomBrandedInstance = useMapGetter(
    'globalConfig/isACustomBrandedInstance'
  );
  const { uiSettings } = useUISettings();

  const { isEnterprise, enterprisePlanName } = useConfig();
  const { accountId } = useAccount();

  const getUserPermissionsForAccount = () => {
    const realPermissions = getUserPermissions(user.value, accountId.value);
    // Only the real instance SuperAdmin may simulate other roles.
    if (user.value?.type !== 'SuperAdmin') return realPermissions;
    const override = uiSettings.value?.view_role_override;
    if (override && VIEW_ROLE_TO_PERMISSIONS[override]) {
      return VIEW_ROLE_TO_PERMISSIONS[override];
    }
    return realPermissions;
  };

  const isFeatureFlagEnabled = featureFlag => {
    if (!featureFlag) return true;
    // SuperAdmin previewing a client view (agent/admin): hide the features that
    // are off-by-default for clients, even if the developer account has them on.
    if (user.value?.type === 'SuperAdmin') {
      const override = uiSettings.value?.view_role_override;
      if (
        VIEW_ROLE_TO_PERMISSIONS[override] &&
        CLIENT_RESTRICTED_FEATURES.includes(featureFlag)
      ) {
        return false;
      }
    }
    return isFeatureEnabled.value(accountId.value, featureFlag);
  };

  const checkPermissions = requiredPermissions => {
    if (!requiredPermissions || !requiredPermissions.length) return true;
    const userPermissions = getUserPermissionsForAccount();
    return hasPermissions(requiredPermissions, userPermissions);
  };

  const checkInstallationType = config => {
    if (Array.isArray(config) && config.length > 0) {
      const installationCheck = {
        [INSTALLATION_TYPES.ENTERPRISE]: isEnterprise,
        [INSTALLATION_TYPES.CLOUD]: isOnChatwootCloud.value,
        [INSTALLATION_TYPES.COMMUNITY]: true,
      };

      return config.some(type => installationCheck[type]);
    }

    return true;
  };

  const isPremiumFeature = featureFlag => {
    if (!featureFlag) return true;
    return PREMIUM_FEATURES.includes(featureFlag);
  };

  const hasPremiumEnterprise = computed(() => {
    if (isEnterprise) return enterprisePlanName !== 'community';

    return true;
  });

  const shouldShow = (featureFlag, permissions, installationTypes) => {
    const flag = unref(featureFlag);
    const perms = unref(permissions);
    const installation = unref(installationTypes);

    // if the user does not have permissions or installation type is not supported
    // return false;
    // This supersedes everything
    if (!checkPermissions(perms)) return false;
    if (!checkInstallationType(installation)) return false;

    if (isACustomBrandedInstance.value) {
      // if this is a custom branded instance, we just use the feature flag as a reference
      return isFeatureFlagEnabled(flag);
    }

    // if on cloud, we should if the feature is allowed
    // or if the feature is a premium one like SLA to show a paywall
    // the paywall should be managed by the individual component
    if (isOnChatwootCloud.value) {
      return isFeatureFlagEnabled(flag) || isPremiumFeature(flag);
    }

    if (isEnterprise) {
      // in enterprise, if the feature is premium but they don't have an enterprise plan
      // we should it anyway this is to show upsells on enterprise regardless of the feature flag
      // Feature flag is only honored if they have a premium plan
      //
      // In case they have a premium plan, the check on feature flag alone is enough
      // because the second condition will always be false
      // That means once subscribed, the feature can be disabled by the admin
      //
      // the paywall should be managed by the individual component
      return (
        isFeatureFlagEnabled(flag) ||
        (isPremiumFeature(flag) && !hasPremiumEnterprise.value)
      );
    }

    // default to true
    return true;
  };

  const shouldShowPaywall = featureFlag => {
    const flag = unref(featureFlag);
    if (!flag) return false;

    if (isACustomBrandedInstance.value) {
      // custom branded instances never show paywall
      return false;
    }

    if (isPremiumFeature(flag)) {
      if (isOnChatwootCloud.value) {
        return !isFeatureFlagEnabled(flag);
      }

      if (isEnterprise) {
        return !hasPremiumEnterprise.value;
      }
    }

    return false;
  };

  return {
    checkPermissions,
    shouldShowPaywall,
    isFeatureFlagEnabled,
    shouldShow,
  };
}
